import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { Check, ChevronRight, History, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useMarkYesterday, yesterdayKey } from '@/hooks/use-mark-yesterday'

const DISMISS_KEY = 'goaltree-yesterday-catchup-dismissed'

function dismissedToday(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === yesterdayKey()
  } catch {
    return false
  }
}

// "Did you do these yesterday?" for habits due yesterday that weren't checked
// off — usually late-night ones remembered the next morning.
export function YesterdayCatchUp({
  habits,
  collapsed = false,
  className,
}: {
  habits: { id: Id<'nodes'>; title: string }[]
  collapsed?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const markYesterday = useMarkYesterday()
  const [hidden, setHidden] = useState(dismissedToday)
  const [marked, setMarked] = useState<Set<string>>(() => new Set())
  const [expanded, setExpanded] = useState(false)
  if (hidden || habits.length === 0) return null
  const shown = habits.slice(0, 3)
  if (shown.every((h) => marked.has(h.id as string))) return null

  if (collapsed && !expanded)
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 rounded-xl border border-border/60 bg-card/50 px-3 py-2 text-left text-[13px] transition-colors hover:bg-muted/50',
          className,
        )}
      >
        <History className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">
          {t('habitYesterday.collapsed', { count: shown.length })}
          <span className="text-muted-foreground"> · {t('habitYesterday.review')}</span>
        </span>
        <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
      </button>
    )

  return (
    <div className={cn('rounded-2xl border border-border bg-card/60 px-3.5 py-3', className)}>
      <div className="flex items-center gap-2">
        <History className="size-4 shrink-0 text-muted-foreground" />
        <p className="min-w-0 flex-1 text-[13px] font-semibold">{t('habitYesterday.title')}</p>
        <button
          type="button"
          onClick={() => {
            try {
              localStorage.setItem(DISMISS_KEY, yesterdayKey())
            } catch {}
            setHidden(true)
          }}
          aria-label={t('common.close')}
          className="flex size-7 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-muted"
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="mt-1.5 flex flex-col">
        {shown.map((h) => {
          const done = marked.has(h.id as string)
          return (
            <div key={h.id} className="flex items-center gap-3 py-1.5">
              <span
                className={cn(
                  'min-w-0 flex-1 truncate text-[13px]',
                  done && 'text-muted-foreground line-through',
                )}
              >
                {h.title}
              </span>
              <button
                type="button"
                disabled={done}
                onClick={async () => {
                  setMarked((prev) => new Set([...prev, h.id as string]))
                  const ok = await markYesterday(h.id, h.title)
                  if (!ok)
                    setMarked((prev) => {
                      const next = new Set(prev)
                      next.delete(h.id as string)
                      return next
                    })
                }}
                className={cn(
                  'flex shrink-0 cursor-pointer items-center gap-1 rounded-full px-3 py-1 text-xs font-bold transition-colors',
                  done
                    ? 'bg-primary/12 text-primary-text'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90',
                )}
              >
                <Check className="size-3.5" strokeWidth={3} />
                {done ? t('habitYesterday.logged') : t('habitYesterday.didIt')}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
